import { expValEquals } from '@confluence/feature-experiments';
import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';

import AgentsWaitingForYourInputSessionsQuery from './__generated__/AgentsWaitingForYourInputSessionsQuery.graphql.ts';
import HomeRecentAgentSessionsQuery from './__generated__/HomeRecentAgentSessionsQuery.graphql.ts';
import type { HomeRecentRovoActivationQuery$data } from './__generated__/HomeRecentRovoActivationQuery.graphql.ts';
import HomeRecentRovoActivationQuery from './__generated__/HomeRecentRovoActivationQuery.graphql.ts';
import {
	AGENTS_WAITING_FOR_YOUR_INPUT_PAGE_SIZE,
	getAgentsWaitingForYourInputSince,
	getRecentAgentSessionsQueryVariables,
} from './recentAgentSessionsQueryHelpers.ts';

export function preloadAgentsWaitingForYourInput(
	args: PreloadAgentsWaitingForYourInputArgs,
): Promise<void | Error[] | unknown[][]> {
	return waitForAllReturnWhenError([
		preloadAgentsWaitingForYourInputSessions(args),
		preloadRecentAgentSessions(args),
	]);
}

export type PreloadAgentsWaitingForYourInputArgs = {
	cloudId: string;
	userId: string;
};

/**
 * Preloads the first page of the sessions. Their associations are loaded when rendering.
 */
async function preloadAgentsWaitingForYourInputSessions({
	cloudId,
	userId,
}: PreloadAgentsWaitingForYourInputArgs): Promise<unknown[]> {
	// SSR preloaders do not receive Admin Hub AI eligibility yet
	if (!expValEquals('cc_ai_agentic_sessions', 'version', 2)) {
		return [];
	}

	const { result: rovoActivationResult, rovoActivationId } = await preloadRovoActivationId(cloudId);
	if (!rovoActivationId) {
		return [rovoActivationResult];
	}

	const sessionsResult = await queryAgg({
		query: AgentsWaitingForYourInputSessionsQuery,
		variables: getRecentAgentSessionsQueryVariables({
			first: AGENTS_WAITING_FOR_YOUR_INPUT_PAGE_SIZE,
			rovoActivationId,
			since: getAgentsWaitingForYourInputSince(),
			userId,
		}),
	});
	return [rovoActivationResult, sessionsResult];
}

async function preloadRecentAgentSessions({
	cloudId,
	userId,
}: PreloadAgentsWaitingForYourInputArgs): Promise<unknown[]> {
	if (
		!expValEquals('cc_ai_agentic_sessions', 'isEnabled', true) ||
		expValEquals('cc_ai_agentic_sessions', 'version', 2)
	) {
		return [];
	}

	const { result: rovoActivationResult, rovoActivationId } = await preloadRovoActivationId(cloudId);
	if (!rovoActivationId) {
		return [rovoActivationResult];
	}

	const sessionsResult = await queryAgg({
		query: HomeRecentAgentSessionsQuery,
		variables: getRecentAgentSessionsQueryVariables({ userId, rovoActivationId }),
	});
	return [rovoActivationResult, sessionsResult];
}

async function preloadRovoActivationId(
	cloudId: string,
): Promise<{ result: unknown; rovoActivationId: string | null | undefined }> {
	const result = await queryAgg({
		query: HomeRecentRovoActivationQuery,
		variables: { cloudId },
	});
	const data = result.data as HomeRecentRovoActivationQuery$data | undefined;
	return { result, rovoActivationId: data?.tenantContexts?.[0]?.activationIdByProduct?.active };
}
